import { useState } from "react";
import { CalendarClock, Check, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, timeAgo } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const STATUS = {
  pending: ["Waiting for confirmation", "bg-white/15 text-white"],
  confirmed: ["Confirmed", "bg-[#d4f253] text-[#0f2a33]"],
  replaced: ["Replaced by a new time", "bg-white/10 text-slate-300"],
};

export function PickupForm({ convId, onDone, onCancel }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [place, setPlace] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/conversations/${convId}/pickup`, { date, time, place: place || null }); toast.success("Pick-up time sent"); onDone(); }
    catch (e2) { toast.error(formatErr(e2)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="pickup-form" onSubmit={submit} className="mx-4 mb-2 space-y-2 rounded-2xl border border-[#d4f253]/40 bg-[#0f2a33] p-3">
      <p className="flex items-center gap-2 text-sm font-bold"><CalendarClock className="h-4 w-4 text-[#d4f253]" />Plan the pick-up</p>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="pickup-date-input" type="date" required min={today()} value={date} onChange={(e) => setDate(e.target.value)} className="field h-10 text-sm" />
        <input data-testid="pickup-time-input" type="time" required value={time} onChange={(e) => setTime(e.target.value)} className="field h-10 text-sm" />
      </div>
      <input data-testid="pickup-place-input" maxLength={120} value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Meeting spot (optional) — a busy public place is safest" className="field h-10 text-sm" />
      <div className="flex gap-2">
        <button data-testid="pickup-send-btn" disabled={busy || !date} className="btn-lime flex-1 py-2 text-xs">Suggest this time</button>
        <button type="button" data-testid="pickup-cancel-btn" onClick={onCancel} className="rounded-xl border border-white/15 px-3 text-xs hover:bg-white/5"><X className="h-4 w-4" /></button>
      </div>
    </form>
  );
}

export function PickupBubble({ m, me, onChange, onReschedule }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/pickups/${m.id}/confirm`); toast.success("Pick-up confirmed!"); onChange(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const [label, cls] = STATUS[m.pickup_status] || STATUS.pending;
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`}>
      <div data-testid={`pickup-bubble-${m.id}`} className="w-72 max-w-[85%] rounded-2xl border border-sky-400/40 bg-[#0f2a33] p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-300"><CalendarClock className="h-4 w-4" />{me ? "You suggested a pick-up" : "Pick-up suggested"}</p>
        <p className="mt-1 font-display text-xl font-extrabold">{fmt(m.pickup_at)}</p>
        {m.place && <p className="mt-1 flex items-center gap-1 text-xs text-slate-300"><MapPin className="h-3.5 w-3.5" />{m.place}</p>}
        <span data-testid={`pickup-status-${m.id}`} className={`mt-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
        {!me && m.pickup_status === "pending" && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid={`pickup-confirm-${m.id}`} disabled={busy} onClick={confirm} className="btn-lime py-2 text-xs"><Check className="h-4 w-4" />Confirm</button>
            <button data-testid={`pickup-reschedule-${m.id}`} onClick={onReschedule} className="rounded-xl border border-white/20 py-2 text-xs font-semibold hover:bg-white/5">Another time</button>
          </div>
        )}
        <p className="mt-2 text-[10px] text-slate-500">{timeAgo(m.created_at)}</p>
      </div>
    </div>
  );
}
